// Training Record emails in batches, not per record (Jill 2026-10-23).
//
//   Reviewers (the mentor; the Deputy Organizer for a licensed deputy; the
//   Training Chair when a trainee has no mentor) get no email per record:
//   the record waits in their review list with a red dot. They are emailed
//   only when their unchecked list goes OVER 100: once when it crosses, then
//   at most one reminder a day while it stays over.
//
//   Training pilots get no email per record sent to them: one digest a day
//   (7 am Alaska), only when new records arrived in their grading queue since
//   the last digest, listing every record waiting.
//
// Plain JS with no imports, so the edge functions (tr_daily_digest,
// tr_notify) and tests/trDigest.test.mjs share it.

export const REVIEW_ALERT_OVER = 100;

// never the shared mailbox (Jill 2026-10-07), on every path (Codex on #614)
export const NEVER_EMAIL = ["seapa.training@gmail.com"];
export const emailable = (addr) => !!addr && !NEVER_EMAIL.includes(String(addr).trim().toLowerCase());

const SIGN_OFF = "\n\nSEAPA Portal (automated)";

// The day in Alaska for a timestamp (or now): "YYYY-MM-DD".
export function alaskaDay(at = new Date()) {
  return new Date(at).toLocaleDateString("en-CA", { timeZone: "America/Juneau" });
}

// The hour in Alaska (0-23). pg_cron runs on UTC, so the 7am job is
// scheduled at both 15:05 and 16:05 UTC and only the run in Alaska's 7
// o'clock hour sends (daylight time and standard time).
export function alaskaHour(at = new Date()) {
  return Number(new Intl.DateTimeFormat("en-US", { timeZone: "America/Juneau", hour: "numeric", hourCycle: "h23" }).format(new Date(at)));
}

// The UTC instant Alaska's current day began (for the once-a-day claim).
export function alaskaMidnightUtc(at = new Date()) {
  const day = alaskaDay(at);
  for (const off of ["08", "09"]) {
    const t = new Date(`${day}T${off}:00:00Z`);
    if (alaskaDay(t) === day && alaskaHour(t) === 0) return t.toISOString();
  }
  return new Date(`${day}T09:00:00Z`).toISOString();
}

// Who reviews a trainee's graded records: the Deputy Organizer(s) for a
// licensed deputy, else the mentor, else the Training Chair(s). The same
// rule as tr_notify's reviewerFor and the editor's paperRecipient.
export function reviewersFor(trainee, { mentorOf = {}, deputyOrganizers = [], chairs = [] } = {}) {
  if (!trainee) return [];
  if (trainee.deputy_license_level) return deputyOrganizers;
  const m = mentorOf[trainee.id];
  return m ? [m] : chairs;
}

// reviewer pilot id -> how many graded records wait for their check
export function reviewQueues(records, traineesById, ctx) {
  const out = {};
  for (const r of records || []) {
    if (r.status !== "graded") continue;
    const t = traineesById[r.trainee_id];
    const who = reviewersFor(t, ctx);
    // an inactive trainee is off the Chair's page, so off the Chair's count;
    // a mentor's or Deputy Organizer's list still shows them (Codex on #614)
    if (t?.is_active === false && who === ctx.chairs) continue;
    for (const id of who) out[id] = (out[id] || 0) + 1;
  }
  return out;
}

// Over 100, and no alert yet today (Alaska): one on crossing, then at most
// one a day while the list stays over.
export function reviewAlertDue(count, lastAlertAt, now = new Date()) {
  if (!(count > REVIEW_ALERT_OVER)) return false;
  return !lastAlertAt || alaskaDay(lastAlertAt) < alaskaDay(now);
}

// A digest is owed when a waiting record has not been listed in one yet.
// Kept by record, not by time: a record whose save was still landing when
// the run read the queue is simply listed next time (Codex on #614).
export function tpDigestDue(waiting, listedIds) {
  if (!waiting?.length) return false;
  const listed = new Set(listedIds || []);
  return waiting.some((r) => !listed.has(r.id));
}

const EVOLUTION_WORDS = {
  dock: "docking", undock: "undocking", moor: "mooring", unmoor: "unmooring", transit: "transit",
  anchor: "anchoring", weigh_anchor: "weighing anchor",
};
export function evolutionWords(evolutions) {
  const words = (evolutions || []).map((e) => EVOLUTION_WORDS[e]).filter(Boolean);
  return words.length ? words.join(", ") : "observation";
}

// waiting: [{ trainee, vessel_name, record_date, evolutions }], oldest first
export function tpDigestEmail(pilotName, waiting, link) {
  const n = waiting.length;
  const lines = waiting.map((r) => `  ${r.record_date || ""}  ${r.trainee || "A trainee"}, ${r.vessel_name || "ship not named"}, ${evolutionWords(r.evolutions)}`);
  return {
    subject: `${n} Training Record${n === 1 ? "" : "s"} waiting for your grade`,
    text: `${pilotName || "Captain"},\n\n${n === 1 ? "A Training Record is" : `${n} Training Records are`} waiting in your TR Grading Queue:\n\n${lines.join("\n")}\n\nGrade and sign them in the SEAPA Portal:\n${link}${SIGN_OFF}`,
  };
}

// link: one page, or several when the count spans the Chair's page and the
// review list
export function reviewAlertEmail(reviewerName, count, link) {
  const links = [].concat(link);
  return {
    subject: `${count} Training Records waiting for your review`,
    text: `${reviewerName || "Captain"},\n\n${count} graded Training Records are waiting for your check. They already count on the trainees' progress; each needs your review signature.\n\nOpen ${links.length > 1 ? "your review lists" : "your review list"} in the SEAPA Portal:\n${links.join("\n")}\n\nThis reminder comes at most once a day while more than ${REVIEW_ALERT_OVER} are waiting.${SIGN_OFF}`,
  };
}
